/**
 * Utility functions for retrying Firestore operations
 */

/**
 * Retry a function with exponential backoff
 * @param operation Function to retry
 * @param maxRetries Maximum number of retries
 * @param initialDelay Initial delay in milliseconds
 * @returns Result of the operation
 */
export async function retryOperation<T>(
  operation: () => Promise<T>,
  maxRetries: number = 3,
  initialDelay: number = 1000
): Promise<T> {
  let retries = 0;
  let delay = initialDelay;
  
  while (true) {
    try {
      return await operation();
    } catch (error: any) {
      retries++;
      
      
      // If we've reached max retries, throw the error
      if (retries >= maxRetries) {
        throw error;
      }
      
      // Wait with exponential backoff
      await new Promise(resolve => setTimeout(resolve, delay));
      delay *= 2; // Exponential backoff
    }
  }
}

/**
 * Retry a Firestore operation with exponential backoff
 * This is specifically for Firestore operations that might fail due to connectivity issues
 */
export async function retryFirestoreOperation<T>(
  operation: () => Promise<T>,
  operationName: string = 'Firestore operation'
): Promise<T> {
  try {
    return await retryOperation(operation, 5, 500);
  } catch (error: any) {
    
    throw error;
  }
}

